import React from 'react'
import {
    Box, Button, IconButton, InputAdornment, LinearProgress, Table, TableBody, TableCell, TableContainer,
    TableHead, TablePagination, TableRow, TextField, Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { ApiPushCreateGroup, ApiPushDeleteGroup, ApiPushGetGroups, ApiPushUpdateGroup } from '../../apiService/Core/Apicall'
import PopupDialog from '../../Components/Popups/ConfirmDialog'
import DarkDialog from '../../Components/Push/DarkDialog'
import PushDisabledNotice, { InlineAlert } from '../../Components/Push/PushDisabledNotice'
import PushPageHeader from '../../Components/Push/PushPageHeader'
import PushPanel from '../../Components/Push/PushPanel'
import { useGuardedFetch } from '../../Components/Push/useGuardedFetch'
import { formFieldSx, headerBtnSx, paginationStyle, searchFieldStyle, tableSx, tdStyle, thStyle, trHover } from '../../Components/Push/pushStyles'
import { fmtDate, fmtNum, getErrorMessage, isPushDisabled, qs, useDebouncedValue } from '../../Components/Push/pushUtils'
import { usePersistedState } from '../../hooks/userPersistedState'
import type { Pagination, PushGroup } from '../../Components/Push/types'

const HEADERS = ['Name', 'Description', 'Members', 'Created by', 'Created at', 'Action']
const NAME_MIN = 2
const NAME_MAX = 80
const DESC_MAX = 300

const Groups: React.FC = () => {
    const navigate = useNavigate()
    const [page, setPage] = usePersistedState('groups:page', 0)
    const [rowsPerPage, setRowsPerPage] = usePersistedState('groups:rowsPerPage', 10)
    const [search, setSearch] = usePersistedState('groups:search', '')
    const q = useDebouncedValue(search.trim())
    const [editing, setEditing] = React.useState<PushGroup | 'new' | null>(null)
    const [name, setName] = React.useState('')
    const [description, setDescription] = React.useState('')
    const [formError, setFormError] = React.useState<string | null>(null)
    const [saving, setSaving] = React.useState(false)
    const [deleting, setDeleting] = React.useState<PushGroup | null>(null)
    const [deleteBusy, setDeleteBusy] = React.useState(false)
    const busyRef = React.useRef(false)

    const fetcher = React.useCallback(
        (silent: boolean) => ApiPushGetGroups(qs({ q, page: page + 1, limit: rowsPerPage }), { silent }),
        [q, page, rowsPerPage]
    )
    const { data, error, loading, refetch } = useGuardedFetch<any>(fetcher, { silentLoads: true })
    const rows: PushGroup[] = data?.data?.items ?? []
    const pagination: Pagination | undefined = data?.meta?.pagination
    const total = pagination?.total ?? 0

    React.useEffect(() => {
        if (data && page > 0 && rows.length === 0) setPage(Math.max(0, Math.ceil(total / rowsPerPage) - 1))
    }, [data, page, rows.length, total, rowsPerPage, setPage])

    const openEditor = (g: PushGroup | 'new') => {
        setEditing(g)
        setName(g === 'new' ? '' : g.name)
        setDescription(g === 'new' ? '' : g.description ?? '')
        setFormError(null)
    }

    const validate = () => {
        const n = name.trim()
        if (n.length < NAME_MIN) return 'Group name is required (at least 2 characters).'
        if (n.length > NAME_MAX) return `Name is too long (max ${NAME_MAX}).`
        if (description.trim().length > DESC_MAX) return `Description is too long (max ${DESC_MAX}).`
        return null
    }

    const onSave = async () => {
        if (busyRef.current || !editing) return
        const v = validate()
        if (v) { setFormError(v); return }
        busyRef.current = true
        setSaving(true)
        setFormError(null)
        const payload = { name: name.trim(), description: description.trim() }
        try {
            if (editing === 'new') await ApiPushCreateGroup(payload)
            else await ApiPushUpdateGroup(editing.id, payload)
            toast.success(editing === 'new' ? 'Group created' : 'Group updated')
            setEditing(null)
            refetch()
        } catch (e) {
            setFormError(getErrorMessage(e))
        } finally {
            busyRef.current = false
            setSaving(false)
        }
    }

    const onDelete = async () => {
        if (!deleting || busyRef.current) return
        busyRef.current = true
        setDeleteBusy(true)
        try {
            await ApiPushDeleteGroup(deleting.id)
            toast.success('Group deleted')
            setDeleting(null)
            refetch()
        } catch (e) {
            toast.error(getErrorMessage(e))
            setDeleting(null)
        } finally {
            busyRef.current = false
            setDeleteBusy(false)
        }
    }

    const disabled = isPushDisabled(error)

    return (
        <div className="common-layout flex-column">
            <PushPageHeader
                title="Groups"
                actions={
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={() => openEditor('new')} disabled={disabled} sx={headerBtnSx}>
                        Create group
                    </Button>
                }
            />

            {disabled ? (
                <PushDisabledNotice />
            ) : (
                <PushPanel
                    title="Custom Groups"
                    icon={<GroupsOutlinedIcon sx={{ color: '#ff6a3d', fontSize: 20 }} />}
                    sx={{ mt: 2 }}
                    bodySx={{ p: 0 }}
                    action={
                        <TextField
                            placeholder="Search groups"
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(0) }}
                            size="small"
                            sx={{ ...searchFieldStyle, width: 280 }}
                            slotProps={{ input: { startAdornment: (<InputAdornment position="start"><SearchIcon className="color-primary" /></InputAdornment>) } }}
                        />
                    }
                >
                    {error && <Box sx={{ p: 2 }}><InlineAlert severity="error">{getErrorMessage(error)}</InlineAlert></Box>}
                    <Box sx={{ height: 3 }}>{loading && <LinearProgress sx={{ height: 3, background: 'transparent', '& .MuiLinearProgress-bar': { background: '#ff6a3d' } }} />}</Box>
                    <TableContainer sx={{ maxHeight: '60vh' }}>
                        <Table stickyHeader sx={tableSx}>
                            <TableHead>
                                <TableRow>{HEADERS.map((h) => <TableCell key={h} sx={thStyle}>{h}</TableCell>)}</TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.length > 0 ? rows.map((g) => (
                                    <TableRow key={g.id} sx={{ ...trHover, cursor: 'pointer' }} onClick={() => navigate(`/admin/groups/${g.id}`)}>
                                        <TableCell sx={tdStyle}>
                                            <Typography fontSize={13} fontWeight={600} color="#e0e0e0">{g.name}</Typography>
                                        </TableCell>
                                        <TableCell sx={{ ...tdStyle, maxWidth: 320, whiteSpace: 'normal' }}>{g.description || '-'}</TableCell>
                                        <TableCell sx={tdStyle}>{fmtNum(g.memberCount)}</TableCell>
                                        <TableCell sx={tdStyle}>{g.createdBy?.name ?? '-'}</TableCell>
                                        <TableCell sx={tdStyle}>{fmtDate(g.createdAt)}</TableCell>
                                        <TableCell sx={tdStyle}>
                                            <Box sx={{ display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                                                <Tooltip title="View members" arrow>
                                                    <IconButton size="small" onClick={() => navigate(`/admin/groups/${g.id}`)}>
                                                        <VisibilityOutlinedIcon sx={{ color: '#ff6a3d', fontSize: 18 }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Rename" arrow>
                                                    <IconButton size="small" onClick={() => openEditor(g)}>
                                                        <EditOutlinedIcon sx={{ color: '#9ca3af', fontSize: 18 }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete group" arrow>
                                                    <IconButton size="small" onClick={() => setDeleting(g)}>
                                                        <DeleteOutlineIcon sx={{ color: '#ef4444', fontSize: 18 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={HEADERS.length} align="center" sx={{ py: 8, background: '#1a1a1a', border: 'none' }}>
                                            <GroupsOutlinedIcon sx={{ color: '#2f2f2f', fontSize: 40, mb: 1 }} />
                                            <Typography color="#4a4a4a" fontSize={13}>
                                                {loading ? 'Loading groups...' : 'No groups yet. Create one to get started.'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div" count={total} page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
                        rowsPerPageOptions={[5, 10, 25, 50]} showFirstButton showLastButton
                        sx={paginationStyle}
                    />
                </PushPanel>
            )}

            <DarkDialog
                open={Boolean(editing)}
                onClose={() => setEditing(null)}
                title={editing === 'new' ? 'Create group' : 'Rename group'}
                icon={<GroupsOutlinedIcon sx={{ fontSize: 18 }} />}
                maxWidth="xs"
                busy={saving}
                confirmText={editing === 'new' ? 'Create' : 'Save'}
                onConfirm={onSave}
            >
                <TextField
                    fullWidth autoFocus label="Name" value={name}
                    onChange={(e) => { setName(e.target.value); setFormError(null) }}
                    helperText={`${name.trim().length}/${NAME_MAX}`}
                    sx={{ ...formFieldSx, mb: 2 }}
                />
                <TextField
                    fullWidth multiline minRows={2} label="Description (optional)" value={description}
                    onChange={(e) => { setDescription(e.target.value); setFormError(null) }}
                    helperText={`${description.trim().length}/${DESC_MAX}`}
                    sx={formFieldSx}
                />
                {formError && <Box sx={{ mt: 2 }}><InlineAlert severity="error">{formError}</InlineAlert></Box>}
            </DarkDialog>

            <PopupDialog
                open={Boolean(deleting)}
                onClose={() => setDeleting(null)}
                onConfirm={onDelete}
                loading={deleteBusy}
                variant="danger"
                title="Delete Group"
                message={`Delete group "${deleting?.name ?? ''}"? Existing campaigns keep their audience history. Users are not deleted.`}
                confirmText="Delete"
            />
        </div>
    )
}

export default Groups
